import {
  IGetStaticDataResponse,
  IGetStepProgressResponse,
  IGetStepProgressFullResponse,
  IGetStepDraftResponse,
  IGetStepDraftFullResponse,
  IGetStepStaticDataResponse,
  ISearchCompanyRequestParams,
  ISearchCompanyResponse,
  ISignupClient,
  ISaveStepProgressPayload,
  IStaticData,
  TCheckoutProductDto,
  IGetCheckoutProductResponse,
  IGetPricingOptionsPayload,
  IPricingOptionsResponse,
  IPaymentIntentStatusResponse,
  IGetDraftPaymentIntentRequestParams,
  IGetDraftPaymentIntentResponse,
  ICompleteSignupResponse,
  TSignupCompletionDto,
  ILookupInvitePayload,
  ILookupInviteResponse,
  TLookupInviteDto,
  TPaymentIntentDto,
  IValidateAccountExistsPayload,
  TValidateAccountExistsDto,
  IGetSignupStatusPayload,
  ISignupStatusResponse,
  TSignupStatusDto,
  TStepContentDto,
  IGetStepContentResponse,
  TAddressSuggestion,
  TAddressLookupResponse,
  IAddressLookupPayload,
  IAddressResponse,
  IGetCountryListResponse,
  ICountryOption,
  IGetDuplicateOrganisationResponse,
  IGetDuplicateOrganisationPayload,
  ILookupOrganisationResponse,
  ILookupOrganisationPayload,
  ILookupOrganisationDTO,
  IValidateVatPayload,
  IValidateVatResponse,
  IValidateVatDTO,
  IGetStepContentProductsPayload,
  IGetStepContentProductsResponse,
  IStepContentProductsDTO,
  IPaymentDetailsContentDTO,
  IGetPaymentDetailsContentResponse,
  IGetCheckoutContentProductResponse,
  ISelectBankTransferPaymentPayload,
  ISelectBankTransferPaymentDto,
  ISelectBankTransferPaymentResponse,
  IUserDetailsContentDTO,
  IGetUserDetailsContentResponse,
  IRelationsContentDTO,
  IGetRelationsContentResponse,
  IOptOutContentDTO,
  IGetOptOutContentResponse,
  IGetStaticDataParams,
  IGetBusinessLinePayload,
  IGetBusinessLineResponse,
  IPaymentIntentStatusWithProductResponse,
  TPaymentIntentStatusWithProductDto
} from "./types";
import {
  IResolvePaymentLinkResponse,
  TResolvePaymentLinkDto,
  TResolvePaymentLinkPayload,
  TGetPaymentIntentPayload,
  IGetPaymentIntentResponse
} from "./paymentLink.types";
import {
  ESignupStep,
  IAddress,
  ICheckoutProductContent,
  IProductPricingSummary
} from "features/common";
import { DEFAULT_SEARCH_COMPANY_PAGING } from "./constants";

export class SignupClient implements ISignupClient {
  constructor(private transport: Common.IHttpClient) {}

  async getStaticData(params?: IGetStaticDataParams): Promise<IStaticData> {
    const path = buildStaticDataPath(params);
    const { data } = await this.transport.get<{ data: IGetStaticDataResponse }>(path);

    return data.result;
  }

  async getStepContent(step: ESignupStep): Promise<TStepContentDto> {
    const { data } = await this.transport.get<{
      data: IGetStepContentResponse;
    }>(`/signup-wizard/step-content/${step}`);

    return data.result;
  }

  async getStepProgress<T, P = unknown>(
    email: string,
    step: ESignupStep
  ): Promise<IGetStepProgressFullResponse<T, P>> {
    const { data } = await this.transport.get<{
      data: IGetStepProgressResponse<T, P>;
    }>(`/signup-wizard/get-progress/${email}/${step}`);

    return data.result;
  }

  async getStepDraft<T, P = unknown>(
    email: string,
    step: ESignupStep
  ): Promise<IGetStepDraftFullResponse<T, P>> {
    const { data } = await this.transport.get<{
      data: IGetStepDraftResponse<T, P>;
    }>(`/signup-draft/get-draft/${email}/${step}`);

    return data.result;
  }

  async getStepStaticData<T>(step: ESignupStep): Promise<T> {
    const { data } = await this.transport.get<{
      data: IGetStepStaticDataResponse<T>;
    }>(`/static-data/${step}`);

    return data.result.stepData;
  }

  async searchCompany(requestParams: ISearchCompanyRequestParams): Promise<ISearchCompanyResponse> {
    const payload: ISearchCompanyRequestParams = {
      ...requestParams,
      paging: {
        ...DEFAULT_SEARCH_COMPANY_PAGING,
        ...requestParams.paging
      }
    };

    const { data } = await this.transport.post<
      { data: ISearchCompanyResponse },
      ISearchCompanyRequestParams
    >("/companies/search", payload);

    return data;
  }

  async saveStepProgress<T>(
    payload: ISaveStepProgressPayload<T>,
    options?: { force?: boolean }
  ): Promise<Common.IBaseApiResponse> {
    const path = options?.force
      ? "/signup-wizard/save-progress?force"
      : "/signup-wizard/save-progress";

    const { data } = await this.transport.post<
      {
        data: Common.IBaseApiResponse;
      },
      ISaveStepProgressPayload<T>
    >(path, payload);

    return data;
  }

  async getCheckoutProduct(email: string): Promise<TCheckoutProductDto> {
    const { data } = await this.transport.post<
      {
        data: IGetCheckoutProductResponse;
      },
      {
        email: string;
      }
    >("/signup-wizard/checkout/product", { email });

    return data.result;
  }

  async getDraftPaymentIntent({
    email,
    paymentMethod,
    setupFutureUsage,
    purchaseOrderNumber
  }: IGetDraftPaymentIntentRequestParams): Promise<TPaymentIntentDto> {
    const { data } = await this.transport.post<
      {
        data: IGetDraftPaymentIntentResponse;
      },
      IGetDraftPaymentIntentRequestParams
    >("/signup-wizard/checkout/draft-intent", {
      email,
      paymentMethod,
      setupFutureUsage: setupFutureUsage ?? true,
      purchaseOrderNumber: purchaseOrderNumber ?? ""
    });

    return data.result;
  }

  async getPaymentIntent({
    invoiceId,
    paymentMethod,
    offSession
  }: TGetPaymentIntentPayload): Promise<IGetPaymentIntentResponse> {
    const { data } = await this.transport.post<
      {
        data: IGetPaymentIntentResponse;
      },
      TGetPaymentIntentPayload
    >("/payment-intent", {
      invoiceId,
      paymentMethod,
      offSession: offSession ?? true
    });

    if (!data.isSuccess) {
      throw new Error(data.message || "GET payment intent failed");
    }

    return data;
  }

  async getPricingOptions(payload: IGetPricingOptionsPayload): Promise<IProductPricingSummary[]> {
    const { data } = await this.transport.post<
      {
        data: IPricingOptionsResponse;
      },
      IGetPricingOptionsPayload
    >("/signup-wizard/pricing-options", payload);

    return data.result;
  }

  async getSignupStatus(email: string): Promise<TSignupStatusDto> {
    const { data } = await this.transport.post<
      {
        data: ISignupStatusResponse;
      },
      IGetSignupStatusPayload
    >("/signup-wizard/status", { email });

    return data.result;
  }

  async completeSignup(email: string): Promise<TSignupCompletionDto> {
    const { data } = await this.transport.post<
      {
        data: ICompleteSignupResponse;
      },
      {
        email: string;
      }
    >("/signup-wizard/finalize-signup", { email });

    return data.result;
  }

  async lookupInvite(params: ILookupInvitePayload): Promise<TLookupInviteDto> {
    const { data } = await this.transport.post<
      { data: ILookupInviteResponse },
      ILookupInvitePayload
    >("/invite/lookup", params);

    return data.result;
  }

  async getPaymentIntentStatus(paymentIntentId: string): Promise<IPaymentIntentStatusResponse> {
    const { data } = await this.transport.get<{
      data: IPaymentIntentStatusResponse;
    }>(`/payment-intent/query?paymentIntentId=${paymentIntentId}`);

    return data;
  }

  async getPaymentIntentStatusWithProduct(
    paymentIntentId: string
  ): Promise<TPaymentIntentStatusWithProductDto> {
    const { data } = await this.transport.get<{
      data: IPaymentIntentStatusWithProductResponse;
    }>(`/payment-intent/query-with-product?paymentIntentId=${paymentIntentId}`);

    if (!data.isSuccess) {
      throw new Error(data.message || "GET payment intent status failed");
    }

    return data.result;
  }

  async validateAccountExists(
    params: IValidateAccountExistsPayload
  ): Promise<TValidateAccountExistsDto> {
    const { data } = await this.transport.post<
      { data: TValidateAccountExistsDto },
      IValidateAccountExistsPayload
    >("/validation/duplicate-account", params);

    return data;
  }

  async addressLookupAutocomplete(params: IAddressLookupPayload): Promise<TAddressSuggestion[]> {
    const { query, countryCode, limit } = params;
    const { data } = await this.transport.get<{ data: TAddressLookupResponse }>(
      `/address-lookup/autocomplete?query=${query}&countryCode=${countryCode}&limit=${limit}`
    );

    if (data.isSuccess) {
      return data.result;
    }

    throw new Error(data.message || "Address lookup failed");
  }

  async getAddress(suggestionId: string, countryCode: string): Promise<IAddress> {
    const { data } = await this.transport.get<{
      data: IAddressResponse;
    }>(`/address-lookup/${suggestionId}?countryCode=${countryCode}`);

    if (data.isSuccess) {
      return data.result;
    }

    throw new Error(data.message || "Unable to fetch address");
  }

  async getCountryList(): Promise<ICountryOption[]> {
    const { data } = await this.transport.get<{
      data: IGetCountryListResponse;
    }>("/address-lookup/countries");

    if (data.isSuccess) {
      return data.result;
    }

    throw new Error(data.message || "Unable to fetch country list");
  }

  async clearProgress(email: string): Promise<Common.IBaseApiResponse> {
    const data = await this.transport.delete<Common.IBaseApiResponse>(
      `/signup-wizard/clear-progress/${email}`
    );
    return data;
  }

  async validateDuplicateOrganisation(params: IGetDuplicateOrganisationPayload): Promise<boolean> {
    const { data } = await this.transport.post<
      { data: IGetDuplicateOrganisationResponse },
      IGetDuplicateOrganisationPayload
    >("/validation/duplicate-organisation", params);

    return data.result;
  }

  async lookupOrganisation(params: ILookupOrganisationPayload): Promise<ILookupOrganisationDTO> {
    const { data } = await this.transport.post<
      { data: ILookupOrganisationResponse },
      ILookupOrganisationPayload
    >("/validation/organisation/lookup", params);

    if (data.isSuccess) {
      return data.result;
    }

    throw new Error(data.message!);
  }

  async validateVat(params: IValidateVatPayload): Promise<IValidateVatDTO> {
    const { data } = await this.transport.post<{ data: IValidateVatResponse }, IValidateVatPayload>(
      "/validation/vat/validate",
      params
    );

    if (data.isSuccess) {
      return data.result;
    }

    throw new Error(data.message!);
  }

  async getStepContentProducts({
    email,
    scope
  }: IGetStepContentProductsPayload): Promise<IStepContentProductsDTO> {
    const { data } = await this.transport.get<{
      data: IGetStepContentProductsResponse;
    }>(`/signup-wizard/step-content/${email}/3/products?slug=${scope}`);

    if (data.isSuccess) {
      return data.result;
    }

    throw new Error(data.message!);
  }

  async getPaymentDetailsContent(): Promise<IPaymentDetailsContentDTO> {
    const { data } = await this.transport.get<{
      data: IGetPaymentDetailsContentResponse;
    }>(`/signup-wizard/step-content/6?slug=payment-details&view=flat`);

    if (data.isSuccess) {
      return data.result;
    }

    throw new Error(data.message!);
  }

  async getCheckoutContentProduct(email: string): Promise<ICheckoutProductContent[]> {
    const { data } = await this.transport.get<{
      data: IGetCheckoutContentProductResponse;
    }>(
      `/signup-wizard/step-content/6?slug=metal-payment-checkout&view=flat&email=${encodeURIComponent(
        email
      )}`
    );

    if (data.isSuccess) {
      return data.result;
    }

    throw new Error(data.message!);
  }

  async selectBankTransferPayment({
    email,
    purchaseOrderNumber
  }: ISelectBankTransferPaymentPayload): Promise<ISelectBankTransferPaymentDto> {
    const { data } = await this.transport.post<
      {
        data: ISelectBankTransferPaymentResponse;
      },
      ISelectBankTransferPaymentPayload
    >("/signup-wizard/checkout/bank-transfer", { email, purchaseOrderNumber });

    if (data.isSuccess) {
      return data.result;
    }

    throw new Error(data.message!);
  }

  async getUserDetailsContent(
    email?: string,
    signupSessionId?: Common.Nullable<string>
  ): Promise<IUserDetailsContentDTO> {
    const emailParam = email ? `&email=${encodeURIComponent(email)}` : "";
    const { data } = await this.transport.get<{
      data: IGetUserDetailsContentResponse;
    }>(
      `/signup-wizard/step-content/1?slug=metal-sign-up&view=flat${emailParam}${
        signupSessionId ? `&signupSessionId=${signupSessionId}` : ""
      }`
    );
    if (data.isSuccess) {
      return data.result;
    }

    throw new Error(data.message!);
  }

  async getRelationsContent(email: string): Promise<IRelationsContentDTO> {
    const { data } = await this.transport.get<{
      data: IGetRelationsContentResponse;
    }>(
      `/signup-wizard/step-content/20?slug=metal-relations&view=flat&email=${encodeURIComponent(
        email
      )}`
    );

    if (data.isSuccess) {
      return data.result;
    }

    throw new Error(data.message!);
  }

  async getOptOutContent(email: string): Promise<IOptOutContentDTO> {
    const { data } = await this.transport.get<{
      data: IGetOptOutContentResponse;
    }>(
      `/signup-wizard/step-content/21?slug=metal-inside-sales-checkout&view=flat&email=${encodeURIComponent(
        email
      )}`
    );

    if (data.isSuccess) {
      return data.result;
    }

    throw new Error(data.message!);
  }

  async getBusinessLine(registrationNumber: string): Promise<number> {
    const { data } = await this.transport.post<
      {
        data: IGetBusinessLineResponse;
      },
      IGetBusinessLinePayload
    >("/companies/business-line", { registrationNumber });

    if (data.isSuccess) {
      return data.result;
    }

    throw new Error(data.message!);
  }

  async resolvePaymentLink(query: string): Promise<TResolvePaymentLinkDto> {
    const { data } = await this.transport.post<
      { data: IResolvePaymentLinkResponse },
      TResolvePaymentLinkPayload
    >("/payment-link/resolve", {
      querystring: query
    });

    if (data.isSuccess) {
      return data.result;
    }

    throw new Error(data.message!);
  }
}

function buildStaticDataPath(params?: IGetStaticDataParams): string {
  if (!params) return "/static-data";

  const { email, sessionId, communityId } = params;

  return ["/static-data", communityId && `byid/${communityId}`, email?.trim() || sessionId?.trim()]
    .filter(Boolean)
    .join("/");
}
